import { useEffect, useState } from 'react';
import {
  FiLayers,
  FiFilter,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiSearch,
  FiDownload,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  ReasonForm,
  Select,
  MultiSelect,
  TableActions,
} from '../../components';
import '../CompanyManager/style.css';

export default function ReasonManager() {
  const permissions = usePermission('management');
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Company management
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  
  // Pagination (client-side for now as API returns all)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const [reasonToDelete, setReasonToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
     try {
       const { data } = await Api.get('/companies');
       setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  useEffect(() => {
    if (companies.length > 0) {
      if (companyFilter !== 'all' && !companies.find(c => String(c.id) === String(companyFilter))) {
        setCompanyFilter('all');
      } else {
        loadReasons();
      }
    }
  }, [companies, companyFilter]);

  async function loadReasons() {
    try {
      setLoading(true);
      setError('');
      let allReasons = [];

      if (companyFilter === 'all') {
        const promises = companies.map(c => 
          Api.getReasons(c.id).catch(err => {
             console.error(`Error loading reasons for company ${c.id}:`, err);
             return [];
          }).then(res => (res || []).map(r => ({ ...r, company: c })))
        );
        const results = await Promise.all(promises);
        allReasons = results.flat();
      } else {
        const company = companies.find(c => String(c.id) === String(companyFilter));
        const res = await Api.getReasons(companyFilter);
        allReasons = (res || []).map(r => ({ ...r, company }));
      }
      
      setReasons(allReasons);
      setHasLoadedOnce(true);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar motivos.');
    } finally {
      setLoading(false);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setCompanyFilter(idsWithoutAll[0]);
    } else {
      setCompanyFilter('all');
    }
  }

  const handleExport = () => {
    if (!reasons || reasons.length === 0) {
        toast.warning('Não há dados para exportar.');
        return;
    }

    const exportData = reasons.map(r => ({
        'Empresa': r.company ? (r.company.trade_name || r.company.corporate_name) : '',
        'Motivo': r.name,
        'Código Folha': r.payroll_code || '',
        'Abona': r.abates ? 'Sim' : 'Não',
        'Impede Ponto': r.prevents_clocking ? 'Sim' : 'Não',
        'Desconta VA': r.deducts_va ? 'Sim' : 'Não',
        'Desconta VT': r.deducts_vt ? 'Sim' : 'Não',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Motivos");
    XLSX.writeFile(wb, "motivos.xlsx");
  };

  function handleCreate() {
    setSelectedReason(null);
    setIsModalOpen(true);
  }

  function handleEdit(reason) {
    setSelectedReason(reason);
    setIsModalOpen(true);
  }

  function handleDeleteClick(reason) {
    setReasonToDelete(reason);
    setIsDeleteModalOpen(true);
  }

  async function confirmDelete() {
    if (!reasonToDelete) return;
    try {
      setIsDeleting(true);
      await Api.deleteReason(reasonToDelete.company_id, reasonToDelete.id);
      toast.success('Motivo removido com sucesso!');
      loadReasons();
      setIsDeleteModalOpen(false);
    } catch (err) {
      toast.error('Erro ao remover motivo.');
    } finally {
      setIsDeleting(false);
      setReasonToDelete(null);
    }
  }

  function handleFormSuccess() {
    setIsModalOpen(false);
    loadReasons();
  }

  const filteredReasons = reasons.filter(r => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      r.name.toLowerCase().includes(searchLower) ||
      (r.payroll_code && r.payroll_code.toLowerCase().includes(searchLower));

    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    const companyId = r.company_id || r.company?.id;
    const matchesCompany =
      hasAllSelected ||
      specificIds.length === 0 ||
      specificIds.includes(String(companyId));

    return matchesSearch && matchesCompany;
  });

  const totalItems = filteredReasons.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedReasons = filteredReasons.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'name',
      header: 'Motivo',
      width: '2fr',
      cellClassName: 'manager-table-cell-primary',
      render: r => (
        <>
          <div className="manager-table-avatar">
            <FiLayers size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {r.name}
            </span>
            {r.payroll_code && (
              <span className="manager-table-subtitle">
                Cód: {r.payroll_code}
              </span>
            )}
          </div>
        </>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      render: r => {
        const companyName = r.company ? (r.company.corporate_name || r.company.trade_name) : 'N/A';
        return <span className="manager-table-text">{companyName}</span>;
      },
    },
    {
        id: 'config',
        header: 'Configurações',
        width: '2fr',
        render: r => {
            const configs = [];
            if (r.abates) configs.push('Abona');
            if (r.prevents_clocking) configs.push('Impede Ponto');
            if (r.deducts_va) configs.push('Desc. VA');
            if (r.deducts_vt) configs.push('Desc. VT');
            return (
              <span className="manager-table-text" style={{ fontSize: '0.85rem' }}>
                {configs.length > 0 ? configs.join(', ') : '-'}
              </span>
            );
        }
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.8fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: r => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(r);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(r);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function ReasonManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLayers size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <ReasonManagerSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiLayers size={22} />
                </div>
                <div>
                  <h1>Motivos</h1>
                  <p>Visualize, filtre e gerencie os motivos de abono e justificativas.</p>
                </div>
              </div>
              <div className="manager-header-actions" style={{ display: 'flex', gap: '8px' }}>
                <Button onClick={handleExport} className="manager-header-button" variant="secondary">
                    <FiDownload size={18} />
                    Exportar
                </Button>
                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                    <FiPlus size={18} />
                    Novo Motivo
                  </Button>
                )}
            </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {reasons.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredReasons.length} de {reasons.length} motivos
                  </span>
                )}
              </div>
              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <MultiSelect
                    id="company-filter"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                      })),
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                    label="Empresa"
                  />
                </div>
                 <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      icon={FiSearch}
                      placeholder="Busque por nome ou código..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </Card>
            
            {error && <div className="error">{error}</div>}

            {!loading && !error && reasons.length === 0 && (
              <div className="empty-state">
                <p>Nenhum motivo cadastrado.</p>
              </div>
            )}

            {!loading && !error && reasons.length > 0 && (
              <Table
                items={paginatedReasons}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={r => r.id}
                emptyMessage="Nenhum motivo encontrado com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedReason ? 'Editar Motivo' : 'Novo Motivo'}
        closeOnOverlayClick={false}
      >
        <ReasonForm
          companyId={companyFilter === 'all' ? '' : companyFilter}
          companies={companies}
          initialData={selectedReason}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja remover o motivo{' '}
            {reasonToDelete && (
              <span className="delete-modal-company-name">
                {reasonToDelete.name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
            <Button 
                type="button" 
                className="cancel-button" 
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
            >
                Cancelar
            </Button>
            <Button 
                type="button" 
                onClick={confirmDelete} 
                disabled={isDeleting}
            >
                {isDeleting ? 'Removendo...' : 'Excluir'}
            </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
